<template>
	<section class="collapsible">
		<button class="collapsible_toggle"
			type="button"
			:class="{ 'is-chevron-right': chevronRight, 'is-heading': heading }"
			:aria-expanded="String(expanded)"
			:aria-controls="bodyId"
			@click="toggle">
			<svg class="chevron" :class="{ 'is-collapsed': !expanded }"
				viewBox="0 0 20 20" fill="none" aria-hidden="true">
				<path d="M5 8l5 5 5-5" stroke="currentColor" stroke-width="1.5"
					stroke-linecap="round" stroke-linejoin="round" />
			</svg>
			<span class="toggle_label">{{ title }}</span>
			<span v-if="$slots.hint && !expanded" class="toggle_hint">
				<slot name="hint" />
			</span>
		</button>
		<transition name="collapsible_height"
			@enter="onEnter"
			@after-enter="onAfterEnter"
			@before-leave="onBeforeLeave"
			@leave="onLeave">
			<div v-show="expanded" :id="bodyId" class="collapsible_body">
				<slot />
			</div>
		</transition>
	</section>
</template>

<script>
// Sección colapsable genérica para los tabs de financialReports. Pensada
// como "modo foco" en pantallas analíticas: el header del toggle queda
// visible siempre (~32px) y el body se anima fuera del flow sin afectar a
// los hermanos que vienen abajo (ej. tabla de evolución).

// Contador module-scoped para generar IDs estables por instancia. Antes
// usábamos `this._uid` (Vue 2 internal), removido en Vue 3. Este patrón
// migra sin cambios.
let _instanceCounter = 0;

export default {
	name: "CollapsibleSection",

	props: {
		title: { type: String, default: "Resumen" },
		// Si viene, el estado expandido/colapsado se persiste en localStorage
		// bajo esa key. Distinta por tab (p.ej. "ventas:summary-expanded")
		// para que cada reporte tenga su propia preferencia.
		storageKey: { type: String, default: "" },
		defaultExpanded: { type: Boolean, default: true },
		// Opt-in: ubica el chevron a la derecha del título (en lugar del default
		// a la izquierda). Sólo lo usa el tab Ventas; el resto conserva el
		// layout original sin tocar nada.
		chevronRight: { type: Boolean, default: false },
		// Opt-in: estiliza el label como un título de sección real (18px/600,
		// color pleno) en lugar del toggle discreto por defecto. Para cuando la
		// sección colapsable ES el encabezado principal de su bloque (Ventas).
		heading: { type: Boolean, default: false },
	},

	data() {
		return {
			expanded: this.defaultExpanded,
			// Liga aria-controls del button con el id del body de forma única
			// dentro del documento.
			bodyId: `collapsible-body-${++_instanceCounter}`,
		};
	},

	created() {
		if (!this.storageKey) return;
		try {
			const saved = localStorage.getItem(this.storageKey);
			if (saved !== null) this.expanded = saved === "true";
		} catch (e) {
			// localStorage puede estar deshabilitado (modo privado, sandbox).
			// Falla silenciosa: nos quedamos con defaultExpanded.
		}
	},

	methods: {
		toggle() {
			this.expanded = !this.expanded;
			if (this.storageKey) {
				try {
					localStorage.setItem(this.storageKey, String(this.expanded));
				} catch (e) { /* noop */ }
			}
			// `change` (no `update:expanded`) porque la prop `expanded` no
			// existe — el componente es uncontrolled. El sufijo `update:*`
			// queda reservado para casos con .sync / v-model:* reales.
			this.$emit("change", this.expanded);
		},

		// Hooks JS de <transition>: animan max-height entre 0 y scrollHeight,
		// porque CSS no anima `auto`. Reseteamos los estilos inline al final
		// para no dejar max-height fijo (rompería el resize del contenido).
		onEnter(el) {
			el.style.maxHeight = "0";
			el.style.opacity = "0";
			el.style.overflow = "hidden";
			// Reflow forzado: el navegador aplica el frame inicial antes
			// de la transición — sin esto, salta directo al final.
			void el.offsetHeight;
			el.style.transition =
				"max-height 280ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease-out";
			el.style.maxHeight = `${el.scrollHeight}px`;
			el.style.opacity = "1";
		},
		onAfterEnter(el) {
			el.style.maxHeight = "";
			el.style.opacity = "";
			el.style.overflow = "";
			el.style.transition = "";
		},
		onBeforeLeave(el) {
			el.style.maxHeight = `${el.scrollHeight}px`;
			el.style.opacity = "1";
			el.style.overflow = "hidden";
		},
		onLeave(el) {
			void el.offsetHeight;
			el.style.transition =
				"max-height 240ms cubic-bezier(0.4, 0, 1, 1), opacity 160ms ease-in";
			el.style.maxHeight = "0";
			el.style.opacity = "0";
		},
	},
};
</script>

<style scoped lang="scss">
.collapsible {
	display: block;
}

// Toggle: chrome thin. Sin border ni shadow propios — la barra es transparent
// y solo gana background en hover/focus. Coherente con .filter_btn del header.
.collapsible_toggle {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 6px 10px;
	background: transparent;
	border: none;
	border-radius: 8px;
	color: var(--report-text-muted-strong);
	font-size: 13px;
	font-weight: 500;
	line-height: 20px;
	cursor: pointer;
	transition: background-color 160ms ease, color 160ms ease;

	&:hover {
		background: var(--report-surface);
		color: var(--report-text);
	}

	&:focus-visible {
		outline: 2px solid var(--report-accent-border);
		outline-offset: 2px;
	}
}

.chevron {
	width: 14px;
	height: 14px;
	flex-shrink: 0;
	// Deceleration easing — micro-interaction whisper, igual que el sistema.
	transition: transform 220ms cubic-bezier(0.4, 0, 0.2, 1);
}

// Chevron a la derecha del texto: reordenamos los flex items sin duplicar
// markup. El gap del toggle mantiene la separación entre el label y el chevron.
.collapsible_toggle.is-chevron-right {
	.toggle_label { order: 1; }
	.toggle_hint { order: 2; }
	.chevron { order: 3; }
}

// Variante "heading": el label se lee como título de sección real (mismo peso
// y color que el viejo título de la tabla), y el chevron crece para acompañar.
.collapsible_toggle.is-heading {
	gap: 10px;

	.toggle_label {
		font-size: 18px;
		font-weight: 600;
		color: var(--report-text);
		letter-spacing: 0;
	}

	.chevron {
		width: 18px;
		height: 18px;
	}
}

.chevron.is-collapsed {
	transform: rotate(-90deg);
}

.toggle_label {
	letter-spacing: 0.01em;
}

.toggle_hint {
	color: var(--report-text-muted);
	font-size: 12px;
	font-weight: 400;
	margin-left: 4px;
	// Pipe sutil entre el title y el hint: estilo "breadcrumb metadata".
	&::before {
		content: "·";
		margin-right: 6px;
		opacity: 0.6;
	}
}

.collapsible_body {
	margin-top: 8px;
}
</style>
